import React, { useEffect, useMemo } from "react";
import SimpleBar from "simplebar-react";
import { Link, useLocation } from "react-router-dom";
import { CSSTransition } from "react-transition-group";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RiInboxArchiveLine } from "react-icons/ri";
import {
  faHandHoldingUsd,
  faTable,
  faUsers,
  faCog,
  faFileAlt,
  faChartLine,
  faBoxes,
  faUserShield,
  faRoute,
  faRobot,
  faLayerGroup,
  faMicrochip,
  faFileInvoiceDollar,
  faAddressBook,
  faFolderOpen,
  faCalculator,
  faServer,
  faUserFriends,
  faMagic,
  faBan,
  faBoxOpen,
  faClipboardCheck,
  faFileContract,
  faChartBar,
  faDesktop,
  faDatabase,
  faBookOpen,
  faLightbulb,
  faComments,
  faFileInvoice,
  faPaintBrush,
  faKeyboard,
  faNewspaper,
  faTicketAlt,
  faChevronDown,
  faChevronRight,
  faBars,
  faAngleLeft,
  faUserCog,
  faUserTie,
  faHandshake,
  faBullhorn,
  faShieldAlt,
  faFilePdf,
  faBrain,
  faHistory,
  faStream,
  faUserLock,
  faFileExport,
} from "@fortawesome/free-solid-svg-icons";
import {
  Nav,
  Badge,
  Image,
  Button,
  Dropdown,
  Navbar,
  Collapse,
} from "@themesberg/react-bootstrap";

import { Routes } from "../../routes";
import InceptionPic from "../../assets/img/logo.png";
import { config } from "../../configs";
import { isProduction } from "../../utils/constant";
import { usePermissions } from "../../context/PermissionContext";
import { archiveAdminModule } from "../../service/api";
import { useArchive } from "../../context/ArchiveContext";
import { useSidebar } from "../../context/SidebarContext";
import { tracker } from "../../service/mixpanel";
import { showNotification } from "../../service/showNotification";
import TooltipWrapper from "../../helper/Tooltip";

import styles from "./index.module.css";

// Single source of truth. Fields:
//   title, link (required); section; icon | image; tooltip; permission;
//   external, target; badge: { text, bg?, color? }; hidden; isBrand
const sidebarItems = [
  {
    title: "Inception Financial",
    link: Routes.DashboardOverview.path,
    image: InceptionPic,
    isBrand: true,
  },

  // ── Users & Access ──────────────────────────────────────────────────────
  {
    title: "Users",
    link: Routes.SubAdmin.path,
    icon: faUsers,
    section: "Users & Access",
    permission: "read:users",
  },
  {
    title: "Access Control",
    link: Routes.AccessControl.path,
    icon: faUserShield,
    section: "Users & Access",
    permission: "read:access_control",
  },
  {
    title: "Master Users",
    link: Routes.MasterUsers.path,
    icon: faUserCog,
    section: "Users & Access",
    permission: "read:master_users",
  },

  // ── Contacts ────────────────────────────────────────────────
  {
    title: "Contacts",
    link: Routes.Contacts.path,
    icon: faAddressBook,
    section: "Contacts",
    permission: "read:contacts",
  },
  {
    title: "Contact Owner",
    link: Routes.ContactOwner.path,
    icon: faUserTie,
    section: "Contacts",
    permission: "read:contact_owner",
  },
  {
    title: "Partner Documents",
    link: Routes.PartnerDocument.path,
    icon: faFolderOpen,
    section: "Contacts",
    permission: "read:partner_document",
  },

  // ── PURL ────────────────────────────────────────────────────────────────
  {
    title: "NDA Form",
    link: Routes.NDAForm.path,
    icon: faFileAlt,
    section: "PURL",
    permission: "read:nda_form",
  },
  {
    title: "Customization Section",
    link: Routes.Customization.path,
    icon: faPaintBrush,
    section: "PURL",
    permission: "read:customization_section",
  },
  {
    title: "Project Input",
    link: Routes.ProjectInput.path,
    icon: faKeyboard,
    section: "PURL",
    permission: "read:project_input",
  },
  {
    title: "Entity Table",
    link: Routes.EntityTable.path,
    icon: faTable,
    section: "PURL",
    permission: "read:entity_table",
  },
  {
    title: "USER Journey",
    link: Routes.USERJourney.path,
    icon: faRoute,
    section: "PURL",
    permission: "read:user_journey",
  },
  {
    title: "Marketing",
    link: Routes.PurlMarketingResources.path,
    icon: faBullhorn,
    section: "PURL",
    permission: "read:marketing",
  },

  // ── Chatbot ──────────────────────────────────────────────────────────────
  {
    title: "Knowledge Base",
    link: Routes.KnowledgeBase.path,
    icon: faBookOpen,
    section: "Chatbot",
    permission: "read:knowledge_base",
    hidden: isProduction,
  },
  {
    title: "Knowledge Gap",
    link: Routes.KnowledgeGap.path,
    icon: faLightbulb,
    section: "Chatbot",
    permission: "read:knowledge_gap",
    hidden: isProduction,
  },
  {
    title: "User Interaction",
    link: Routes.UserInteraction.path,
    icon: faComments,
    section: "Chatbot",
    permission: "read:user_interaction",
    hidden: isProduction,
  },

  // ── Assets ──────────────────────────────────────────────────────────────
  {
    title: "DO NOT USE - ASSETS V1",
    link: Routes.Assets.path,
    icon: faBan,
    section: "Assets",
    permission: "read:do_not_use_-_assets_v1",
  },
  {
    title: "Assets v2",
    link: Routes.AssetsV2.path,
    icon: faBoxOpen,
    section: "Assets",
    permission: "read:assets_v2",
  },
  {
    title: "Assets Audit",
    link: Routes.AssetAudit.path,
    icon: faClipboardCheck,
    section: "Assets",
    permission: "read:assets_audit",
  },
  {
    title: "Assets Underwriting",
    link: Routes.AssetsUnderwriting.path,
    icon: faFileContract,
    section: "Assets",
    permission: "read:assets_underwriting",
  },
  {
    title: "Divisor Lookup",
    link: Routes.DivisorLookup.path,
    icon: faCalculator,
    section: "Assets",
    permission: "read:divisor_lookup",
  },
  {
    title: "P&C Documents",
    link: Routes.PandCDocuments.path,
    icon: faShieldAlt,
    section: "Assets",
    permission: "read:p&c_documents",
  },
  {
    title: "M1 & M2 Documents",
    link: Routes.M1M2Documents.path,
    icon: faFilePdf,
    section: "Assets",
    permission: "read:m1_&_m2_documents",
  },
  {
    title: "Asset Archive Files",
    link: Routes.AssetArchiveFiles.path,
    icon: faFolderOpen,
    section: "Assets",
    permission: "read:asset_archive_files",
  },
  {
    title: "IRS Package",
    link: Routes.IrsPackage.path,
    icon: faFileInvoiceDollar,
    section: "Assets",
    permission: "read:irs_package",
  },

  // ── Production Reports ──────────────────────────────────────────────────
  // {
  //   title: "Production Reporting",
  //   link: Routes.ProductionReporting.path,
  //   icon: faChartBar,
  //   section: "Production Reports",
  //   permission: "read:production_reporting",
  // },
  // {
  //   title: "Production Monitoring...",
  //   tooltip: "Production Monitoring And Reporting",
  //   link: Routes.ProductionMonitoringAndReporting.path,
  //   icon: faDesktop,
  //   section: "Production Reports",
  //   permission: "read:production_monitoring_and_reporting",
  // },
  // {
  //   title: "Production Monitoring Data",
  //   tooltip: "Production Monitoring Data",
  //   link: Routes.ProductionReportingData.path,
  //   icon: faDatabase,
  //   section: "Production Reports",
  //   permission: "read:production_monitoring_data",
  // },
  // {
  //   title: "Production Reporting",
  //   link: Routes.ProductionReporting.path,
  //   icon: faChartBar,
  //   section: "Production Reports",
  //   permission: "read:production_reporting",
  // },
  {
    title: "Production Monitoring and R..",
    tooltip: "Production Monitoring And Reporting ",
    link: Routes.ProductionMonitoringAndReporting.path,
    icon: faDesktop,
    section: "Production Reports",
    permission: "read:production_monitoring_and_reporting",
  },
  {
    title: "Production Monitoring Data",
    tooltip: "Production Monitoring Data ",
    link: Routes.ProductionReportingData.path,
    icon: faDatabase,
    section: "Production Reports",
    permission: "read:production_monitoring_data",
  },

  // ── Tranches ─────────────────────────────────────────────────────────────
  {
    title: "Tranches",
    link: Routes.Tranches.path,
    icon: faLayerGroup,
    section: "Tranches",
    permission: "read:tranches",
  },
  {
    title: "Tranche Bot",
    link: Routes.TrancheBot.path,
    icon: faRobot,
    section: "Tranches",
    permission: "read:tranche_bot",
  },
  {
    title: "Tranche Bot V2",
    link: Routes.TrancheBotV2.path,
    icon: faRobot,
    section: "Tranches",
    permission: "read:tranche_bot_v2",
  },

  // ── TSA ──────────────────────────────────────────────────────────────────
  {
    title: "TSA Backend",
    link: Routes.TsaBackend.path,
    icon: faCog,
    section: "TSA",
    permission: "read:tsa_backend",
  },
  {
    title: "Tsa Billing",
    link: Routes.TsaBilling.path,
    icon: faFileInvoiceDollar,
    section: "TSA",
    permission: "read:tsa_billing",
  },

  // ── Proforma ─────────────────────────────────────────────────────────────
  {
    title: "Proforma Backend",
    link: Routes.BootstrapTables.path,
    icon: faServer,
    section: "Proforma",
    permission: "read:proforma_backend",
  },
  {
    title: "Proforma Users",
    link: Routes.ProformaUsers.path,
    icon: faUserFriends,
    section: "Proforma",
    permission: "read:proforma_users",
  },
  {
    title: "Proforma Generations",
    link: Routes.ProformaGenerations.path,
    icon: faMagic,
    section: "Proforma",
    permission: "read:proforma_generations",
  },

  // ── Logs ──────────────────────────────────────────────────────────────────
  {
    title: "Asset Logs",
    link: Routes.AssetLogs.path,
    icon: faHistory,
    section: "Logs",
    permission: "read:asset_logs",
  },
  {
    title: "Production Report Logs",
    link: Routes.ProductionReportLogs.path,
    icon: faStream,
    section: "Logs",
    permission: "read:production_report_logs",
  },
  //----Power BI---------------------------------
  {
    title: "Power BI Permission",
    link: Routes.PowerBIPermission.path,
    icon: faUserLock,
    section: "Power BI",
    permission: "read:power_bi_permission",
  },
  {
    title: "Power BI",
    link: Routes.PowerBI.path,
    icon: faChartBar,
    section: "Power BI",
    permission: "read:power_bi",
  },

  // ── General ───────────────────────────────────────────────────────────────
  {
    title: "Report List",
    link: Routes.Transactions.path,
    icon: faFileInvoice,
    section: "General",
    permission: "read:report_list",
  },

  {
    title: "New Reports",
    link: Routes.NewReports.path,
    icon: faNewspaper,
    section: "General",
    permission: "read:new_reports",
  },
  {
    title: "Reports Center",
    link: Routes.ReportsCenter.path,
    icon: faFileExport,
    section: "General",
    permission: "read:reports_center",
  },
  {
    title: "Ticket Center",
    link: Routes.TicketCenter.path,
    icon: faTicketAlt,
    section: "General",
    permission: "read:ticket_center",
  },
];

const archivedNameToRoute = Object.fromEntries(
  sidebarItems.map((i) => [i.title, i.link])
);

const groupBySection = (items) => {
  const sections = [];
  const byName = new Map();
  for (const item of items) {
    const name = item.section || "";
    if (!byName.has(name)) {
      byName.set(name, sections.length);
      sections.push({ name, items: [] });
    }
    sections[byName.get(name)].items.push(item);
  }
  return sections;
};

const Sidebar = () => {
  const { pathname } = useLocation();
  const { hasPermission, isAdmin } = usePermissions();
  const { archivedTasks, loadingArchivedTasks, fetchArchivedTasks } =
    useArchive();
  const {
    isSectionCollapsed,
    toggleSection,
    archiveOpen,
    setArchiveOpen,
    mobileOpen: show,
    setMobileOpen: setShow,
    closeSidebar: closeMobile,
    desktopCollapsed,
    toggleDesktopCollapsed,
  } = useSidebar();

  useEffect(() => {
    if (desktopCollapsed) {
      document.body.classList.add("sidebar-collapsed");
    } else {
      document.body.classList.remove("sidebar-collapsed");
    }
    return () => document.body.classList.remove("sidebar-collapsed");
  }, [desktopCollapsed]);

  const archivedTitles = useMemo(
    () => new Set(archivedTasks.map((t) => t.name)),
    [archivedTasks]
  );
  const sections = useMemo(() => groupBySection(sidebarItems), []);

  const canShowItem = (item) => {
    if (item.hidden) return false;
    if (item.isBrand) return true;
    if (archivedTitles.has(item.title)) return false;
    return !item.permission || hasPermission(item.permission);
  };

  const handleUnarchive = async (taskName) => {
    tracker.unArchivedModule.unArchivedModuleClick(taskName);
    try {
      await archiveAdminModule({ name: taskName, isArchived: false });
      fetchArchivedTasks();
      showNotification("success", `${taskName} unarchived successfully`);
    } catch (err) {
      console.error("Unarchive failed", err);
      showNotification("error", "Failed to unarchive task");
    }
  };

  const renderNavItem = (item) => {
    const linkProps = item.external
      ? { href: item.link }
      : { as: Link, to: item.link };

    const isActive = !item.isBrand && item.link === pathname;
    const iconClass = item.isBrand ? styles.brandIcon : styles.icon;
    const itemClass = [
      isActive ? "active" : "",
      item.isBrand ? styles.brandItem : "",
    ]
      .filter(Boolean)
      .join(" ");

    const node = (
      <Nav.Item key={item.title} className={itemClass} onClick={closeMobile}>
        <Nav.Link {...linkProps} target={item.target}>
          <span className={styles.itemInner}>
            {item.icon && (
              <span className={iconClass}>
                <FontAwesomeIcon icon={item.icon} />
              </span>
            )}
            {item.image && (
              <span className={iconClass}>
                <Image
                  src={item.image}
                  width={20}
                  height={20}
                  className={item.isBrand ? styles.brandImage : undefined}
                />
                {item.isBrand && (
                  <button
                    type="button"
                    className={`${styles.brandToggleBtn} d-none d-md-flex`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleDesktopCollapsed();
                    }}
                    aria-label={
                      desktopCollapsed ? "Expand sidebar" : "Collapse sidebar"
                    }
                    title={
                      desktopCollapsed ? "Expand sidebar" : "Collapse sidebar"
                    }
                  >
                    <FontAwesomeIcon
                      icon={desktopCollapsed ? faBars : faAngleLeft}
                    />
                  </button>
                )}
              </span>
            )}
            {item.isBrand ? (
              <span className={styles.brandTextWrap}>
                <span className="sidebar-text">{item.title}</span>
                {config.NODE_ENV && config.NODE_ENV !== "production" && (
                  <span className={`sidebar-text ${styles.envChip}`}>
                    {config.NODE_ENV}
                  </span>
                )}
              </span>
            ) : (
              <span className="sidebar-text">{item.title}</span>
            )}
          </span>
          {item.badge && (
            <Badge
              pill
              bg={item.badge.bg || "secondary"}
              text={item.badge.color || "primary"}
              className="badge-md notification-count ms-2"
            >
              {item.badge.text}
            </Badge>
          )}
        </Nav.Link>
      </Nav.Item>
    );

    const tooltipText =
      !item.isBrand && desktopCollapsed
        ? item.tooltip || item.title
        : item.tooltip;

    return tooltipText ? (
      <TooltipWrapper key={item.title} tooltip={tooltipText} placement="right">
        {node}
      </TooltipWrapper>
    ) : (
      node
    );
  };

  const renderSectionHeader = (label, collapsed, onToggle) => (
    <div
      role="button"
      tabIndex={0}
      className={styles.sectionHeader}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <span className={styles.sectionLabel}>{label}</span>
      <FontAwesomeIcon
        icon={collapsed ? faChevronRight : faChevronDown}
        size="xs"
        className={styles.sectionChevron}
      />
    </div>
  );

  const permittedArchived = archivedTasks.filter((t) =>
    hasPermission(`read:${t.name.toLowerCase().replace(/\s+/g, "_")}`)
  );

  return (
    <>
      <Navbar
        expand={false}
        collapseOnSelect
        variant="dark"
        className="navbar-theme-primary px-4 d-md-none"
      >
        <Navbar.Brand
          className="me-lg-5"
          as={Link}
          to={Routes.DashboardOverview.path}
        >
          <Image src={InceptionPic} className="navbar-brand-light" />
        </Navbar.Brand>
        <Navbar.Toggle
          as={Button}
          aria-controls="main-navbar"
          onClick={() => setShow(!show)}
        >
          <span className="navbar-toggler-icon" />
        </Navbar.Toggle>
      </Navbar>

      <CSSTransition timeout={300} in={show} classNames="sidebar-transition">
        <SimpleBar
          className={`collapse ${
            show ? "show" : ""
          } sidebar d-md-block bg-primary text-white ${
            desktopCollapsed ? styles.sidebarCollapsed : ""
          }`}
        >
          <div className="sidebar-inner pt-3 d-flex flex-column min-vh-100">
            <Nav
              className={`${styles.sidebarNav} flex-column pt-3 pt-md-0 flex-grow-1 d-flex`}
            >
              {sections.map(({ name, items }) => {
                const visible = items.filter(canShowItem);
                if (visible.length === 0) return null;
                const collapsed = isSectionCollapsed(name);

                return (
                  <div key={name || "__root"}>
                    {name &&
                      renderSectionHeader(name, collapsed, () =>
                        toggleSection(name)
                      )}
                    <Collapse in={!collapsed} appear>
                      <div>{visible.map(renderNavItem)}</div>
                    </Collapse>
                  </div>
                );
              })}

              {hasPermission("read:archived") && (
                <div className="mt-auto">
                  {renderSectionHeader("Archived", !archiveOpen, () =>
                    setArchiveOpen(!archiveOpen)
                  )}
                  <Collapse in={archiveOpen}>
                    <div id="archive-collapse">
                      {loadingArchivedTasks ? (
                        <div className={styles.archiveEmpty}>
                          Loading archived tasks...
                        </div>
                      ) : permittedArchived.length === 0 ? (
                        <div className={styles.archiveEmpty}>No archived</div>
                      ) : (
                        permittedArchived.map((task) => {
                          const route = archivedNameToRoute[task.name] || "#";
                          const isActive = route === pathname;
                          return (
                            <div
                              key={task._id || task.name}
                              className={styles.archiveRow}
                            >
                              <Nav.Item
                                className={`${isActive ? "active" : ""} ${
                                  styles.archiveRowLink
                                }`}
                              >
                                <Nav.Link as={Link} to={route}>
                                  <span className={styles.itemInner}>
                                    <span className={styles.icon}>
                                      <FontAwesomeIcon icon={faTable} />
                                    </span>
                                    <span
                                      className={`sidebar-text ${styles.truncatedText}`}
                                      title={task.name}
                                    >
                                      {task.name}
                                    </span>
                                  </span>
                                </Nav.Link>
                              </Nav.Item>

                              {isAdmin && (
                                <div
                                  role="button"
                                  tabIndex={0}
                                  title="Unarchive"
                                  onClick={() => handleUnarchive(task.name)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      handleUnarchive(task.name);
                                    }
                                  }}
                                  className={styles.unarchiveBtn}
                                >
                                  <RiInboxArchiveLine />
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </Collapse>
                  <Dropdown.Divider className="my-1 border-indigo" />
                </div>
              )}
            </Nav>
          </div>
        </SimpleBar>
      </CSSTransition>
    </>
  );
};

export default Sidebar;
export { sidebarItems };
